import { TrendingUp, TrendingDown, BarChart3 } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from 'recharts';
import { usePriceMovers, type PriceMover } from '@/hooks/usePriceMovers';
import { formatPrice } from '@/lib/card-prices';

export function PriceMoversCard() {
  const { data, isLoading } = usePriceMovers(5);

  if (isLoading) {
    return (
      <div className="rounded-xl bg-background/60 border border-border p-3">
        <p className="text-[10px] text-muted-foreground font-body">Loading price movers…</p>
      </div>
    );
  }

  const hasData = data?.hasHistory && (data.gainers.length || data.losers.length);

  if (!hasData) {
    return (
      <div className="rounded-xl bg-background/60 border border-dashed border-border p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-ink-emerald" />
            <TrendingDown className="h-3.5 w-3.5 text-ink-ruby" />
            <span className="text-[11px] font-medium text-foreground font-body">Weekly gainers & losers</span>
          </div>
          <span className="text-[9px] uppercase tracking-wider text-muted-foreground font-body">Building</span>
        </div>
        <p className="text-[10px] text-muted-foreground font-body">
          Collecting price history nightly — your weekly winners will appear here once we have ~7 days of data.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-background/60 border border-border p-3 space-y-3">
      <div className="flex items-center gap-1.5">
        <BarChart3 className="h-3.5 w-3.5 text-primary" />
        <span className="text-[11px] font-medium text-foreground font-body">Weekly price movers</span>
      </div>

      {data!.gainers.length > 0 && (
        <MoverList title="Top Gainers" tone="up" items={data!.gainers} />
      )}
      {data!.losers.length > 0 && (
        <MoverList title="Top Losers" tone="down" items={data!.losers} />
      )}
    </div>
  );
}

function MoverList({
  title,
  tone,
  items,
}: {
  title: string;
  tone: 'up' | 'down';
  items: PriceMover[];
}) {
  const Icon = tone === 'up' ? TrendingUp : TrendingDown;
  const color = tone === 'up' ? 'text-ink-emerald' : 'text-ink-ruby';
  const stroke = tone === 'up' ? 'hsl(var(--ink-emerald))' : 'hsl(var(--ink-ruby))';
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className={`h-3 w-3 ${color}`} />
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-body">{title}</span>
      </div>
      <ul className="space-y-1.5">
        {items.map(m => (
          <li key={m.print_id} className="flex items-center gap-2 rounded-lg bg-card/60 px-2 py-1.5">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] text-foreground font-body truncate">{m.card_name}</p>
              <p className="text-[9px] text-muted-foreground font-body truncate">{m.set_name}</p>
            </div>
            <div className="h-7 w-16 flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={m.series}>
                  <YAxis hide domain={['dataMin', 'dataMax']} />
                  <Tooltip
                    cursor={false}
                    contentStyle={{ display: 'none' }}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke={stroke}
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="text-right">
              <p className="text-[11px] text-foreground font-body tabular-nums">{formatPrice(m.current_price)}</p>
              <p className={`text-[9px] font-body tabular-nums ${color}`}>
                {m.pct > 0 ? '+' : ''}{m.pct.toFixed(1)}%
              </p>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
